import { getCourseId, getFirm, get } from './SCORM';
import fetchContent from './fetchContent';
import getContentBase from './getContentBase';
import findTopWindow from './findTopWindow';
import getQueryParameter from './getQueryParameter';
import { getTranslations } from './callModContent';

const firm = getFirm();
const courseId = getCourseId();
const beta = getQueryParameter('beta', findTopWindow());
const audioDoc = Boolean(getQueryParameter('audioDoc'));
const ucAuthorAudio = Boolean(getQueryParameter('ucAuthorAudio'));
const clientEditMode = getFirm() && get('clientEditMode');

const topWindow = findTopWindow();
const authoringLite = Boolean((topWindow as EditorWindow)?.__AUTHORING_LITE__);
const previewDraft = Boolean(getQueryParameter('previewDraft'));

type TSV = Record<string, any>;

export default async function loadTranslations(
  languages: string[],
  baseCourseId: number | undefined
) {
  const translations = {};
  const r1: TSV = import.meta.glob<string>('../translations/*.tsv');
  const r2: TSV = import.meta.glob<string>('../customizations/**/*.tsv');

  // console.log({ r1, r2 });

  const loadCodebasePhrases = (glob: TSV, { getCustom = false } = {}) => {
    return Promise.all(
      Object.keys(glob).map(async path => {
        const [, pathId, lang] = path.match(/(\d+)_([\w-]+)/) || [];

        if (parseInt(pathId) !== courseId) return false;
        if (getCustom && !path.toUpperCase().includes(`/${firm}/`))
          return false;

        if (languages.includes(lang)) {
          translations[lang] ??= {};

          try {
            const { default: tsv } = await glob[path]();

            Object.keys(tsv).forEach(k => {
              if (tsv[k]) translations[lang][k] = tsv[k];
            });
          } catch (e) {
            console.error(e);
          }
        }
      })
    );
  };

  type Source = 'base' | 'custom' | 'framework';

  const loadS3Phrases = ({ source }: { source?: Source } = {}) => {
    return Promise.all(
      languages.map(async lang => {
        let base = getContentBase();

        // change internal/live in the path for beta framework translations
        if (source === 'framework' && beta) {
          base = base.replace(/\/content\/(internal|live)/, '/content/beta');
        }

        // load the translations from the various sources
        // base-course is sometimes specified in the yaml and not indicated by the course ID (e.g. duplicated courses in Portal)
        const path =
          source === 'framework'
            ? `${base}/translations/framework_${lang}.tsv.json`
            : source === 'custom'
              ? `${base}/translations/custom_${firm.toUpperCase()}_${courseId}_${lang}.tsv.json`
              : baseCourseId
                ? `${base}/translations/${baseCourseId}_${lang}.tsv.json`
                : `${base}/translations/${courseId}_${lang}.tsv.json`;

        try {
          const raw = await fetchContent(path);
          const tsv = JSON.parse(raw);
          translations[lang] ??= {};

          Object.keys(tsv).forEach(k => {
            if (tsv[k]) translations[lang][k] = tsv[k];
          });
        } catch (e) {
          console.error(e);
        }
      })
    );
  };

  const loadCustomPhrases = () => {
    return Promise.all(
      languages.map(async lang => {
        const rows = await getTranslations({ to: lang });
        if (!rows) return;
        translations[lang] ??= {};

        for (const row of rows) {
          if (row.firmCode === firm) {
            translations[lang][row.key] = row.translation;
          }
        }
      })
    );
  };

  // load the translations in priority order, so customisations replace base text, and S3 TSV.JSON replaces any local TSVs (where keys match)
  await loadCodebasePhrases(r1);
  await loadCodebasePhrases(r2, { getCustom: true });
  await loadS3Phrases({ source: 'framework' });
  await loadS3Phrases({ source: 'base' });
  if (firm !== '___' && !clientEditMode && !authoringLite && !previewDraft)
    await loadS3Phrases({ source: 'custom' });

  if (
    clientEditMode ||
    authoringLite ||
    previewDraft ||
    (ucAuthorAudio && audioDoc)
  )
    await loadCustomPhrases();

  return translations;
}
